import { useCallback, useLayoutEffect, useRef, useState } from "react";

/**
 * Measures an element's own inline size.
 *
 * The app's layout decisions belong to the panel, not the window: content width
 * is the viewport minus a sidebar whose presence is itself viewport-dependent.
 * Keying a table's density off `window.innerWidth` produced a layout that got
 * *worse* at 1024px, where the rail appears and takes 15rem out of the panel the
 * table actually lives in. Asking the container removes that coupling — the rail
 * collapsing, the detail grid splitting, and a narrow window all reach the same
 * code path because they all mean "this box got narrower".
 *
 * `null` until the first measurement, so callers can hold their layout decision
 * until they know the answer rather than rendering the wrong tier and snapping.
 */
export function useContainerWidth() {
  const [width, setWidth] = useState(null);
  const observerRef = useRef(null);

  const ref = useCallback((node) => {
    observerRef.current?.disconnect();
    observerRef.current = null;

    if (!node) return;

    // Seed synchronously so the first paint already has a tier; the observer
    // alone would render once at `null` and correct itself a frame later.
    setWidth(node.getBoundingClientRect().width);

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      // `borderBoxSize` is the box the children actually have to fit inside.
      const next = entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width;
      setWidth((current) => (Math.abs((current ?? 0) - next) < 1 ? current : next));
    });

    observer.observe(node);
    observerRef.current = observer;
  }, []);

  useLayoutEffect(() => () => observerRef.current?.disconnect(), []);

  return [ref, width];
}
